import * as React from 'react'
import cn from 'classnames'
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { Loader } from 'src/design-system'
import { useDndSensors } from 'src/design-system/hooks/use-dnd-sensors'
import { Chat, ChatHandle } from './Chat'
import { CurrentOrgSummary } from './current-org-summary'
import { SortableAnalysisCard } from './analysis-card/SortableAnalysisCard'
import { SkillsSidebar } from './SkillsSidebar'
import { useWorkforceIntelligence } from './useWorkforceIntelligence'
import { WfiContext } from './WfiContext'
import styles from './WorkforceIntelligence.module.scss'
import { ChatSidebar } from './ChatSidebar'
import { OrgSummary } from './OrgSummarySidebar'

/**
 * This component is rendered by Rails as the user's first view into Workforce Intelligence.
 * It calls `useWorkforceIntelligence`, the main hook.
 * State is made available to the "logical grouping" sort of components
 * (like AnalysisCard and CurrentOrgSummary) through the context API.
 * These components then transform data as necessary and pass it
 * to generalized presentational components through props.
 */
export const WorkforceIntelligence = () => {
  const wfi = useWorkforceIntelligence()
  const chatRef = React.useRef<ChatHandle>(null)
  // once we have analyses, whether to show the current chat on the left
  // or the list of previous chats.
  const [showChatList, setShowChatList] = React.useState(false)
  const [showSkillsSidebar, setShowSkillsSidebar] = React.useState(false)
  const sensors = useDndSensors()
  const closeSkillsSidebar = (changedSkills: Map<number, number>) => {
    setShowSkillsSidebar(false)
    wfi.updateSkillRequirements(changedSkills)
  }

  const switchChat = (chatId: number | null, orgSnapshotId: number | null) => {
    setShowChatList(false)

    if (chatId !== wfi.currentChatId) {
      wfi.switchChat(chatId, orgSnapshotId)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = wfi.analyses.findIndex((a) => a.id === active.id)
    const newIndex = wfi.analyses.findIndex((a) => a.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      wfi.reorderAnalyses(oldIndex, newIndex)
    }
  }

  const hasAnalyses = wfi.analyses.length > 0
  const sortableIds = wfi.analyses.map((a) => a.id)

  return (
    <WfiContext.Provider value={wfi}>
      <div className={cn(styles.container, hasAnalyses && styles.hasAnalyses)}>
        {/* TODO:Chat should pull messages/sendMessage from context */}
        {(!hasAnalyses || showChatList) && (
          <ChatSidebar onSwitchChat={switchChat} />
        )}
        {!showChatList && (
          <Chat
            id={wfi.currentChatId}
            ref={chatRef}
            loading={wfi.loading}
            messages={wfi.messages}
            onAdjustSkills={() => setShowSkillsSidebar(true)}
            onBack={() => setShowChatList(true)}
            onSubmit={wfi.sendMessage}
            view={hasAnalyses ? 'side' : 'full'}
          />
        )}
        {hasAnalyses && (
          <div className={styles.analyses}>
            {/* TODO:CurrentOrgSummary should pull teams from context */}
            <CurrentOrgSummary teams={wfi.orgSnapshot?.teams ?? []} />
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortableIds}
                strategy={verticalListSortingStrategy}
              >
                {wfi.analyses.map((analysis) => (
                  <SortableAnalysisCard
                    id={analysis.id}
                    key={analysis.id}
                    onAnchorClick={() =>
                      chatRef.current?.scrollToMessage(
                        String(analysis.messageId)
                      )
                    }
                    title={analysis.name}
                    teams={analysis.teams}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}

        {!hasAnalyses &&
          (wfi.orgSnapshot == null ? (
            <Loader className={styles.orgSummary} />
          ) : (
            <OrgSummary
              avgSalary={wfi.orgSnapshot.avgSalary}
              numRoles={wfi.orgSnapshot.numRoles}
              orgSize={wfi.orgSnapshot.orgSize}
              totalCost={wfi.orgSnapshot.totalCost}
              teams={wfi.orgSnapshot.teams}
              timestamp={wfi.orgSnapshot.timestamp}
            />
          ))}
      </div>
      {/* TODO: SkillsSidebar should pull skills/skillrequirements from context */}
      {showSkillsSidebar && (
        <SkillsSidebar
          currentRequirements={wfi.skillRequirements}
          onClose={closeSkillsSidebar}
          skills={wfi.orgSnapshot?.skills ?? []}
        />
      )}
    </WfiContext.Provider>
  )
}
